const SESSION_LAYOUT_MODE_KEY = 'hangout_layout_mode';

export const LAYOUT_MODES = Object.freeze({
    VN: 'vn',
    CHATBOT: 'chatbot',
    NONE: 'none'
});

const LAYOUT_CYCLE = Object.freeze([
    LAYOUT_MODES.CHATBOT,
    LAYOUT_MODES.VN,
    LAYOUT_MODES.NONE
]);

const normalizeLayoutMode = (mode) => {
    if (mode === LAYOUT_MODES.CHATBOT) return LAYOUT_MODES.CHATBOT;
    if (mode === LAYOUT_MODES.VN) return LAYOUT_MODES.VN;
    if (mode === LAYOUT_MODES.NONE) return LAYOUT_MODES.NONE;
    return null;
};

const getStoredLayoutMode = () => {
    try {
        const stored = sessionStorage.getItem(SESSION_LAYOUT_MODE_KEY);
        if (!stored) return null;
        return normalizeLayoutMode(stored);
    } catch {
        return null;
    }
};

const persistLayoutMode = (mode) => {
    try {
        sessionStorage.setItem(SESSION_LAYOUT_MODE_KEY, mode);
    } catch {
        // Ignore storage failures (private mode/quota).
    }
};

export const getCurrentLayoutMode = () => {
    const globalMode = normalizeLayoutMode(window.__hangoutLayoutMode);
    if (globalMode) return globalMode;
    const storedMode = getStoredLayoutMode();
    if (storedMode) return storedMode;
    return LAYOUT_MODES.CHATBOT;
};

export const applyLayoutModeClasses = (mode) => {
    if (typeof document === 'undefined') return;
    const normalizedMode = normalizeLayoutMode(mode);
    const root = document.body || document.documentElement;
    if (!root) return;

    root.classList.toggle('layout-vn', normalizedMode === LAYOUT_MODES.VN);
    root.classList.toggle('layout-chatbot', normalizedMode === LAYOUT_MODES.CHATBOT);
    root.classList.toggle('layout-none', normalizedMode === LAYOUT_MODES.NONE);
};

export const setCurrentLayoutMode = (mode, options = {}) => {
    const normalizedMode = normalizeLayoutMode(mode) || LAYOUT_MODES.CHATBOT;
    const shouldPersist = options.persist !== false;
    const shouldEmit = options.emit !== false;

    window.__hangoutLayoutMode = normalizedMode;
    if (shouldPersist) persistLayoutMode(normalizedMode);
    applyLayoutModeClasses(normalizedMode);

    if (shouldEmit && typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('hangout:layout-mode-changed', {
            detail: { mode: normalizedMode }
        }));
    }

    return normalizedMode;
};

export const ensureSessionLayoutMode = () => {
    const existingMode = getStoredLayoutMode();
    if (existingMode) {
        return setCurrentLayoutMode(existingMode, { persist: false, emit: false });
    }

    return setCurrentLayoutMode(LAYOUT_MODES.CHATBOT, { persist: true, emit: false });
};

export const getNextLayoutMode = (mode = getCurrentLayoutMode()) => {
    const normalizedMode = normalizeLayoutMode(mode) || LAYOUT_MODES.CHATBOT;
    const index = LAYOUT_CYCLE.indexOf(normalizedMode);
    return LAYOUT_CYCLE[(index + 1) % LAYOUT_CYCLE.length];
};

export const isVnLayoutMode = () => getCurrentLayoutMode() === LAYOUT_MODES.VN;
